import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import CAppSubTitle from '../../../../app/components/CAppSubTitle';
import investmentTypeHistoryLang from '../../features/fund-type/langs/investmentTypeHistoryLang';
import CHistoryTimeline from '../../features/fund-type/components/history/CHistoryTimeline';
import CHistoryInflowOutflow from '../../features/fund-type/components/history/CHistoryInflowOutflow';
import CHistoryJCurve from '../../features/fund-type/components/history/CHistoryJCurve';
import CHistoryTotalValue from '../../features/fund-type/components/history/CHistoryTotalValue';
import IInvestExpertiseFilters from '../../interfaces/expertise/IInvestExpertiseFilters';
import useInvestExpertiseHistoryFlows from '../../hooks/expertise/useInvestExpertiseHistoryFlows';
import IFund from '../../interfaces/fund/IFund';
import * as XLSX from 'xlsx';
import _ from 'lodash';
import useInvestExpertiseHistoryStatsByPeriods from '../../hooks/expertise/useInvestExpertiseHistoryStatsByPeriods';
import { Fab, useTheme, useMediaQuery, SxProps, Box } from '@mui/material';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import investmentTableLang from '../../features/fund-type/langs/investmentTableLang';
import IFundTypeHistoryExport from '../../interfaces/expertise/history/IFundTypeHistoryExport';
import moment from 'moment';
import { removeAccents } from 'remove-accents-esm';
import investmentTypeDomainLang from '../../features/fund-type/langs/investmentTypeDomainLang';
import investmentTypeArticleLang from '../../features/fund-type/langs/investmentTypeArticleLang';
import investmentTypeLang from '../../features/fund-type/langs/investmentTypeLang';
import investmentFundTypeHistoryFlowLang from '../../features/fund-type/langs/investmentFundTypeHistoryFlowLang';
import IFundType from '../../interfaces/expertise/IFundType';
import IFundTypeHistoryPeriod from '../../interfaces/expertise/history/IFundTypeHistoryPeriod';
import CHistoryMultipleNetOfFees from '../../features/fund-type/components/history/CHistoryMultipleNetOfFees';
import langInvestWordLang from '../../langs/langInvestWordLang';
import useInvestExpertiseHistoryByFundByInvestor from '../../hooks/expertise/useInvestExpertiseHistoryByFundByInvestor';
import { langLookup } from '../../../../app/utils/langLookup';

type VInvestExpertiseHistoryProps = {
	fundType: IFundType;
	listFunds: IFund[];
	filters: IInvestExpertiseFilters;
	requestFilters: IInvestExpertiseFilters;
	callExportData: boolean;
	resetCallExportData: () => void;
};

const VInvestExpertiseHistory = (props: VInvestExpertiseHistoryProps) => {
	const { fundType, listFunds, requestFilters } = props;

	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const fundTypeHistoryFlows = useInvestExpertiseHistoryFlows(requestFilters);
	const investExpertiseHistoryByFundByInvestor = useInvestExpertiseHistoryByFundByInvestor(requestFilters);

	const { results: fundTypeHistoryPeriods } = useInvestExpertiseHistoryStatsByPeriods(requestFilters);

	const [nbYearsToDisplay, setNbYearsToDisplay] = useState(3); // Nombre d'années affichées
	useEffect(() => {
		if (isMobile) {
			setNbYearsToDisplay(1);
		} else {
			setNbYearsToDisplay(3);
		}
	}, [isMobile]);

	const [position, setPosition] = useState(0);
	const [years, setYears] = useState<number[] | null>(null); // Années inversées
	useEffect(() => {
		if (fundTypeHistoryPeriods) {
			setYears(_.orderBy(_.uniq(fundTypeHistoryPeriods.map((x) => x.year)), [], 'desc'));
		}
	}, [fundTypeHistoryPeriods]);

	// Initialisation de la position par défaut en fonction du filtre reportingPeriod
	useEffect(() => {
		if (years && requestFilters.reportingPeriod) {
			const defaultFilterYear = Number.parseInt(requestFilters.reportingPeriod.slice(0, 4));
			const index = years.findIndex((x) => x === defaultFilterYear);
			setPosition(index);
		}
	}, [requestFilters.reportingPeriod, years]);

	const [selectedYears, setSelectedYears] = useState<number[] | null>(null);
	useEffect(() => {
		if (years) {
			setSelectedYears(years.slice(position, position + nbYearsToDisplay));
		}
	}, [years, position, nbYearsToDisplay]);

	const [historyPeriods, setHistoryPeriods] = useState<IFundTypeHistoryPeriod[] | null>(null);
	useEffect(() => {
		if (fundTypeHistoryPeriods && selectedYears) {
			const fundTypeHistoryPeriodsTmp = _.orderBy(
				fundTypeHistoryPeriods
					.filter((x) => selectedYears.includes(x.year))
					.map((x) => ({
						...x,
						stat: {
							...x.stat,
						},
						funds: x.funds.filter((f) => f.calledCapital !== null || f.commitments !== null),
					})),
				['year', 'quarter'],
				['asc', 'asc'],
			);
			setHistoryPeriods(fundTypeHistoryPeriodsTmp);
		}
	}, [fundTypeHistoryPeriods, selectedYears]);

	const sxFab = {
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.primary.main,
		boxShadow: theme.palette.mode === 'light' ? '0 5px 15px rgba(0,0,0,0.05)' : '0 5px 15px rgba(0,0,0,0.5)',
		transform: 'translateZ(0)',
		transition: 'all ease-in-out 0.2s',
		'&:disabled': {
			display: 'none',
			opacity: 1,
			backgroundColor: '#DDD',
		},
		':hover': {
			backgroundColor: theme.palette.background.paper,
			transform: 'scale(1.1) translateZ(0)',
		},
	} as SxProps;

	const btnPrevious = (
		<Fab
			className="graph-btn-previous"
			color="primary"
			sx={sxFab}
			onClick={() => setPosition(position + 1)}
			disabled={!!years && years.length - position <= nbYearsToDisplay}
		>
			<AppCustomIcon tag="arrow-left" />
		</Fab>
	);

	const btnNext = (
		<Fab
			className="graph-btn-next"
			color="primary"
			sx={sxFab}
			onClick={() => setPosition(position - 1)}
			disabled={position === 0}
		>
			<AppCustomIcon tag="arrow-right" />
		</Fab>
	);

	useEffect(() => {
		if (
			props.callExportData &&
			investExpertiseHistoryByFundByInvestor &&
			listFunds &&
			fundTypeHistoryFlows &&
			historyPeriods
		) {
			props.resetCallExportData();

			const fundTypeHistoryExports: IFundTypeHistoryExport[] = [];

			fundTypeHistoryFlows.funds.forEach((fundRow) => {
				const fund = listFunds.find((x) => x.id === fundRow.fundId);
				if (!fund || !fund.investors) return;
				const fundInvestor = fund.investors.find((x) => x.company.id === fundRow.investorId);

				if (fundInvestor === undefined) {
					throw new Error(
						'[fundTypeHistoryFlows] Unable to find investorId ' +
							fundRow.investorId +
							' in funds (investors)',
					);
				}
				const fundTypeHistoryExport: IFundTypeHistoryExport = {
					groupName: fundInvestor.company.group.name,
					investorName: fundInvestor.company.name,
					fundName: fund.name,
					fundType: fund.typeTag,
					domainName: fund.domainTag,
					sfdr: fund.sfdr,
					vintage: fund.vintage,
					investmentYear: fundInvestor.investmentYear,
					isRealized: fund.isRealized,
					quarter: fundRow.period?.name ?? '',
					date: moment(fundRow.date).toDate(),
					year: fundRow.period?.year ?? 0,
					data_type: fundRow.typeTag,
					value: fundRow.amount,
				};
				fundTypeHistoryExports.push(fundTypeHistoryExport);
			});

			investExpertiseHistoryByFundByInvestor.funds.forEach((fundRow) => {
				const fund = listFunds.find((x) => x.id === fundRow.fundId);
				if (!fund || !fund.investors) return;
				const fundInvestor = fund.investors.find((x) => x.company.id === fundRow.investorId);
				if (fundInvestor === undefined) {
					throw new Error(
						'[investExpertiseHistoryByFundByInvestor] Unable to find investorId ' +
							fundRow.investorId +
							' in funds (investors)',
					);
				}
				let fundTypeHistoryExport: IFundTypeHistoryExport = {
					groupName: fundInvestor.company.group.name,
					investorName: fundInvestor.company.name,
					fundName: fund.name,
					fundType: fund.typeTag,
					domainName: fund.domainTag,
					sfdr: fund.sfdr,
					vintage: fund.vintage,
					investmentYear: fundInvestor.investmentYear,
					isRealized: fund.isRealized,
					quarter: fundRow.period?.name ?? '',
					date: moment(fundRow.date).toDate(),
					year: fundRow.period?.year ?? 0,
					data_type: 'total_value',
					value: fundRow.totalValue,
				};
				fundTypeHistoryExports.push(fundTypeHistoryExport);

				fundTypeHistoryExport = {
					groupName: fundInvestor.company.group.name,
					investorName: fundInvestor.company.name,
					fundName: fund.name,
					fundType: fund.typeTag,
					domainName: fund.domainTag,
					sfdr: fund.sfdr,
					vintage: fund.vintage,
					investmentYear: fundInvestor.investmentYear,
					isRealized: fund.isRealized,
					quarter: fundRow.period?.name ?? '',
					date: moment(fundRow.date).toDate(),
					year: fundRow.period?.year ?? 0,
					data_type: 'net_called_capital',
					value: fundRow.netCalledCapital,
				};
				fundTypeHistoryExports.push(fundTypeHistoryExport);
			});

			const groupNames = removeAccents(_.uniq(fundTypeHistoryExports.map((x) => x.groupName)).join(' - '));
			const maxDateNames = moment(_.max(fundTypeHistoryExports.map((x) => x.date))).format('YYYY-MM-DD');
			const fileName = `${maxDateNames} - ${groupNames} - History.xlsx`;

			const headers = [
				t(investmentTableLang.group.tag),
				t(investmentTableLang.investor.tag),
				t(investmentTableLang.fund.tag),
				t(investmentTableLang.fund_type.tag),
				t(investmentTableLang.domain.tag),
				t(investmentTableLang.sfdr.tag),
				t(investmentTableLang.vintage.tag),
				t(investmentTableLang.investment_year.tag),
				t(investmentTableLang.realized.tag),
				t(investmentTableLang.reporting_period.tag),
				t(investmentTableLang.date.tag),
				t(investmentTableLang.year.tag),
				t(investmentTableLang.data_type.tag),
				t(investmentTableLang.values.tag),
			];

			const fundTypeHistoryExportsClean = _.orderBy(
				fundTypeHistoryExports.map((x) => ({
					groupName: x.groupName,
					investorName: x.investorName,
					fundName: x.fundName,
					fundType: x.fundType
						? t(langLookup(investmentTypeLang, x.fundType)?.tag ?? '')
						: t(langInvestWordLang.nc.tag),
					domainName:
						x.domainName && langLookup(investmentTypeDomainLang, x.domainName)
							? t(langLookup(investmentTypeDomainLang, x.domainName)?.tag ?? '')
							: t(langInvestWordLang.nc.tag),
					sfdr:
						x.sfdr && langLookup(investmentTypeArticleLang, x.sfdr + '_short')
							? t(langLookup(investmentTypeArticleLang, x.sfdr + '_short')?.tag ?? '')
							: t(langInvestWordLang.na.tag),
					vintage: x.vintage,
					investmentYear: x.investmentYear,
					isRealized: x.isRealized,
					quarter: x.quarter,
					date: moment(x.date).format('DD/MM/YYYY'),
					year: x.year,
					data_type: t(langLookup(investmentFundTypeHistoryFlowLang, 'type_' + x.data_type)?.tag ?? ''),
					value: x.value,
				})),
				['date', 'data_type'],
				['desc', 'asc'],
			);

			const worksheet = XLSX.utils.json_to_sheet(fundTypeHistoryExportsClean);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio');

			worksheet['!cols'] = _.keys(fundTypeHistoryExportsClean[0]).map((field, index) => {
				const header = headers[index] ?? '';
				return {
					wch: Math.max(
						header.length,
						Math.max(
							...fundTypeHistoryExportsClean.map(
								(obj: Record<string, unknown>) => String(obj[field] ?? '').length,
							),
						),
					),
				};
			});

			XLSX.writeFileXLSX(workbook, fileName);
		}
	}, [
		props.callExportData,
		investExpertiseHistoryByFundByInvestor,
		listFunds,
		fundTypeHistoryFlows,
		historyPeriods,
		props,
		t,
	]);

	return (
		<Box id="expertise-content-history">
			{fundType.expertiseHistoryBlocks.includes('timeline') ? (
				<>
					<CAppSubTitle
						title={t(investmentTypeHistoryLang.title_investments_timeline.tag)}
						subtitle={
							<CAppInfoTooltip
								title={t(investmentTypeHistoryLang.title_investments_timeline_tooltip.tag)}
							/>
						}
					/>
					<CHistoryTimeline historyPeriods={historyPeriods} btnPrevious={btnPrevious} btnNext={btnNext} />
				</>
			) : (
				<></>
			)}

			{fundType.expertiseHistoryBlocks.includes('jcurve') ? (
				<>
					<CAppSubTitle
						title={t(investmentTypeHistoryLang.title_jcurve_visualization.tag)}
						subtitle={
							<CAppInfoTooltip
								title={t(investmentTypeHistoryLang.title_jcurve_visualization_tooltip.tag)}
							/>
						}
					/>
					<CHistoryJCurve
						allHistoryPeriods={fundTypeHistoryPeriods}
						historyPeriods={historyPeriods}
						btnPrevious={btnPrevious}
						btnNext={btnNext}
					/>
				</>
			) : (
				<></>
			)}

			{fundType.expertiseHistoryBlocks.includes('total_value') ? (
				<>
					<CAppSubTitle
						title={t(investmentTypeHistoryLang.title_total_value.tag)}
						subtitle={
							<CAppInfoTooltip
								title={
									<div
										dangerouslySetInnerHTML={{
											__html: t(investmentTypeHistoryLang.title_total_value_tooltip.tag, {
												interpolation: { escapeValue: false },
											}),
										}}
									/>
								}
							/>
						}
					/>
					<CHistoryTotalValue
						allHistoryPeriods={fundTypeHistoryPeriods}
						historyPeriods={historyPeriods}
						btnPrevious={btnPrevious}
						btnNext={btnNext}
					/>
				</>
			) : (
				<></>
			)}

			{fundType.expertiseHistoryBlocks.includes('multiple_net_of_fees') ? (
				<>
					<CAppSubTitle
						title={t(investmentTypeHistoryLang.title_multiple_net_of_fees.tag)}
						subtitle={
							<CAppInfoTooltip
								title={
									<Box
										dangerouslySetInnerHTML={{
											__html: t(
												investmentTypeHistoryLang.title_multiple_net_of_fees_tooltip.tag,
												{
													interpolation: { escapeValue: false },
												},
											),
										}}
									/>
								}
							/>
						}
					/>
					<CHistoryMultipleNetOfFees
						allHistoryPeriods={fundTypeHistoryPeriods}
						historyPeriods={historyPeriods}
						btnPrevious={btnPrevious}
						btnNext={btnNext}
					/>
				</>
			) : (
				<></>
			)}

			{fundType.expertiseHistoryBlocks.includes('last_inflows_outflows_engagements') ? (
				<>
					<CAppSubTitle title={t(investmentTypeHistoryLang.title_last_inflows_outflows_engagements.tag)} />
					<CHistoryInflowOutflow listFunds={listFunds} fundTypeHistoryFlows={fundTypeHistoryFlows} />
				</>
			) : (
				<></>
			)}
		</Box>
	);
};

export default VInvestExpertiseHistory;
